/**
 *  @file       SidebarWorker.jsx
 *  @author     Eddie Roosenmaallen <eddie@kingsds.network>
 *  @date       June 2022
 *
 *  This widget presents a simplified worker interface for the app sidebar
 */

import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useDCPWorker } from '@/hooks/useDCPWorker';
import { Button } from '@/components/Elements';
import { AccountsContext } from '@/features/accounts';

import './SidebarWorker.css';

const { VITE_ASSETS_PATH } = import.meta.env;

/**
 *  This widget shows a status indicator for the sidebar worker
 *
 *  @param  {object} props Expected properties:second
 */
const SidebarWorkerStatus = (props) => {
  const { t } = useTranslation();
  const { waiting, working, fetching, submitting, error } = props;
  let classes = [];
  let title = t('idle');
  let text = '\xA0';

  if (waiting)
  {
    classes.push('waiting');
    title = t('waiting_for_work');
    text = t('waiting');
  }
  else if (working) {
    classes.push('working');
    text = t('working');
  }

  if (fetching)
  {
    classes.push('fetching');
    title = t('fetching_task');
    text = t('fetching');
  }
  if (submitting)
  {
    classes.push('submitting');
    title = t('submitting_results');
    text = t('submitting');
  }

  if (error && error.ts > Date.now() - 10000)
  {
    classes = ['error'];
    title = error.error.message;
    text = t('error');
  }

  const className = classes.length ? classes.join(' ') : 'idle';

  return (
    <span id='progress-thinger' title={title} className={className} style={className.includes('working') ? {background: `url(${VITE_ASSETS_PATH}/img/dcp-gradient.png)`} : {}}>{text}</span>
  );
};

/**
 * Render a widget that that shows the status of the worker and allows the user
 * to start and stop it.
 *
 * @param  {object} _props Component properties.
 * @return {JSX.Element}   The rendered component.
 */
export const SidebarWorker = (_props) => {
  const { t } = useTranslation();
  const accounts = useContext(AccountsContext);

  if (!accounts?.[0])
    throw new Error('No account to deposit credits into', );

  const {
    workerState,
    workerStatistics,
    toggleWorker,
  } = useDCPWorker({
    workerOptions: {
      paymentAddress: accounts[0].address,
    },
  });

  // show how many workers are working in the title
  useEffect(() => {
    document.title = `DCP Portal | ${workerState.workingSandboxes} working`
  }, [workerState.workingSandboxes]);


  return (
    <div className='workerStatus'>
      <table>
        <tbody>
          <tr>
            <td>{t('slices')}</td>
            <td>{workerStatistics.slices}</td>
          </tr>
          <tr>
            <td>{t('credits')}</td>
            <td>{workerStatistics.credits.toFixed(3)}</td>
          </tr>
          <tr className='workerBtn'>
            <td><SidebarWorkerStatus working={!!workerState.workingSandboxes} waiting={workerState.working && !workerState.workingSandboxes} fetching={workerState.fetching} submitting={workerState.submitting} error={workerState.error} /></td>
            <td><Button id='workerStatusButton' text={workerState.working ? t('stop_sidebar') : t('start_sidebar')} colour={true} onClick={() => toggleWorker()} /></td>
          </tr>
        </tbody>
      </table>
    </div>);
}
